import React, { useEffect, useState } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Modal, { ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens';
import {
	SOFTWARE_PROJECT,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	type ProjectType,
} from '@atlassian/jira-common-constants';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { main_upgradeOnboardingExperiment_UpgradeOnboardingModal$key } from '@atlassian/jira-relay/src/__generated__/main_upgradeOnboardingExperiment_UpgradeOnboardingModal.graphql';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { fireButtonAnalytics } from '../../common/utils';
import { fetchSiteTraits } from '../../services';
import { UPGRADE_ONBOARDING_PRODUCT_TRAIT_MAP } from '../../services/fetch-upgrade-traits/constants';
import { projectTypeToApplicationKey } from '../../utils';
import { Carousel } from './carousel';
import messages from './messages';
import { TimelineSection } from './timeline-section';

const SOURCE_NAME = 'upgradeOnboarding';

export type UpgradeOnboardingModalProps = {
	projectType: ProjectType;
	addPaymentMethodRef: main_upgradeOnboardingExperiment_UpgradeOnboardingModal$key;
	onDismiss: () => void;
	onClose?: () => void;
	transactionAccountId?: string | null;
};

export const UpgradeOnboardingModal = ({
	projectType,
	addPaymentMethodRef,
	onDismiss,
	onClose: onCloseCallback,
	transactionAccountId,
}: UpgradeOnboardingModalProps) => {
	const [isOpen, setIsOpen] = useState(true);
	const [messageContent, setMessageContent] = useState(messages.softwareAutoUpgradeDescription);
	const [headingContent, setHeadingContent] = useState('');
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode } = useThemeObserver();
	const { cloudId, productEntitlementDetails } = useTenantContext();

	const data = useFragment<main_upgradeOnboardingExperiment_UpgradeOnboardingModal$key>(
		graphql`
			fragment main_upgradeOnboardingExperiment_UpgradeOnboardingModal on CommerceExperienceCapability {
				experienceUrl
			}
		`,
		addPaymentMethodRef,
	);
	const product = projectTypeToApplicationKey(projectType);
	const trialEndTime = productEntitlementDetails?.[product]?.trialEndTime;

	const origin = new OriginTracer({ product: 'jira' });
	const isDarkMode = colorMode === 'dark';
	const redirectUrl = origin.addToUrl(data?.experienceUrl || '');

	const onClose = () => {
		setIsOpen(false);
		onCloseCallback?.();
		onDismiss();
	};

	const onClickAddPaymentDetailsLink = () => {
		fireButtonAnalytics(createAnalyticsEvent, 'addPaymentDetails', {});
		setIsOpen(false);
		onCloseCallback?.();
	};

	const closeModal = () => {
		fireButtonAnalytics(createAnalyticsEvent, 'dismissUpgradeOnboardingModal', {});
		onClose();
	};

	useEffect(() => {
		(async () => {
			let traits = await fetchSiteTraits(cloudId);

			const possibleTraits = new Set(UPGRADE_ONBOARDING_PRODUCT_TRAIT_MAP.get(product));
			let foundTraits = traits.filter((trait) => possibleTraits.has(trait.name));

			// for manual upgrade from billing console (CCP), the trait is recorded against the transactionAccountId
			if (foundTraits.length === 0 && transactionAccountId) {
				traits = await fetchSiteTraits(transactionAccountId);
				foundTraits = traits.filter((trait) => possibleTraits.has(trait.name));
			}

			if (foundTraits.length >= 1) {
				// sort by date and get the latest upgrade trait. if its just one trait array doesn't change
				foundTraits.sort((a, b) => new Date(b.value).getTime() - new Date(a.value).getTime());

				const isAutoUpgrade = foundTraits[0].name.includes('auto_upgrade');

				if (projectType === SOFTWARE_PROJECT) {
					if (isAutoUpgrade) {
						setHeadingContent(formatMessage(messages.headingAutoUpgrade));
						setMessageContent(messages.softwareAutoUpgradeDescription);
					} else {
						setHeadingContent(formatMessage(messages.headingManualUpgrade, { product: 'Jira' }));
						setMessageContent(messages.softwareManualUpgradeDescription);
					}
				} else if (projectType === SERVICE_DESK_PROJECT) {
					if (isAutoUpgrade) {
						setHeadingContent(formatMessage(messages.headingAutoUpgrade));
						setMessageContent(messages.serviceDeskAutoUpgradeDescription);
					} else {
						setHeadingContent(
							formatMessage(messages.headingManualUpgrade, {
								product: 'Jira Service Management',
							}),
						);
						setMessageContent(messages.serviceDeskManualUpgradeDescription);
					}
				} else if (projectType === PRODUCT_DISCOVERY_PROJECT) {
					if (isAutoUpgrade) {
						setHeadingContent(formatMessage(messages.headingAutoUpgrade));
						setMessageContent(messages.productDiscoveryAutoUpgradeDescription);
					} else {
						setHeadingContent(
							formatMessage(messages.headingManualUpgrade, {
								product: 'Jira Product Discovery',
							}),
						);
						setMessageContent(messages.productDiscoveryManualUpgradeDescription);
					}
				}
			}
		})();
	}, [cloudId, formatMessage, product, projectType, transactionAccountId]);

	return (
		<ContextualAnalyticsData sourceName={SOURCE_NAME} sourceType={MODAL}>
			<ModalTransition>
				{isOpen && (
					<Modal
						onClose={onClose}
						width={960}
						autoFocus={false}
						shouldScrollInViewport
						testId="upgrade-onboarding-experiment.ui.upgrade-onboarding-modal.upgrade-modal"
					>
						<FireScreenAnalytics
							attributes={{
								originProduct: 'jira',
								originIdGenerated: origin?.id,
							}}
						/>
						<Box xcss={BodyContainerStyles}>
							<Box xcss={LeftBodyPanelStyles}>
								<Box>
									<Inline xcss={TitleWrapperStyles}>
										<ModalTitle>
											<Heading size="large">{headingContent}</Heading>
										</ModalTitle>
									</Inline>
									<Inline xcss={DescriptionStyles}>
										<FormattedMessage {...messageContent} />
									</Inline>
									<TimelineSection trialEndTime={trialEndTime} />
								</Box>
								<Box xcss={ButtonGroupContainerStyles}>
									<Button onClick={closeModal}>{formatMessage(messages.later)}</Button>
									<Button
										testId="upgrade-onboarding-experiment.ui.upgrade-onboarding-modal.cta-button"
										appearance="primary"
										href={redirectUrl}
										onClick={onClickAddPaymentDetailsLink}
										target="_blank"
									>
										{formatMessage(messages.addPaymentDetails)}
									</Button>
								</Box>
							</Box>
							<Box
								xcss={[
									RightBodyPanelStyles,
									isDarkMode ? RightBodyPanelBackgroundDarkStyles : RightBodyPanelBackgroundStyles,
								]}
							>
								<Carousel projectType={projectType} />
							</Box>
						</Box>
					</Modal>
				)}
			</ModalTransition>
		</ContextualAnalyticsData>
	);
};

export default UpgradeOnboardingModal;

const TitleWrapperStyles = xcss({
	marginBottom: 'space.200',
});

const BodyContainerStyles = xcss({
	display: 'flex',
	position: 'relative',
});

const DescriptionStyles = xcss({
	marginTop: 'space.0',
	marginBottom: 'space.100',
	maxWidth: '382px',
});

const LeftBodyPanelStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	minHeight: '536px',
	justifyContent: 'space-between',
	flex: '1',
	flexBasis: '480px',
	padding: 'space.600',
	boxSizing: 'border-box',
	color: 'color.text',
});

const RightBodyPanelStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	minHeight: '536px',
	justifyContent: 'end',
	flex: '1',
	flexBasis: '480px',
	padding: 'space.500',
	boxSizing: 'border-box',
	color: 'color.text',
	borderTopLeftRadius: 'space.0',
	borderTopRightRadius: '3px',
	borderBottomRightRadius: '3px',
	borderBottomLeftRadius: 'space.0',
});

const RightBodyPanelBackgroundStyles = xcss({
	background: 'linear-gradient(84deg, #E9F2FF 0%, #F3F0FF 100%)',
});

const RightBodyPanelBackgroundDarkStyles = xcss({
	background: 'linear-gradient(84deg, #092957 0%, #2B2451 100%)',
});

const ButtonGroupContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'right',
	gap: 'space.150',
});
